import type { CaseRecord, RecordFact } from "../../types";
import {
  dedupeFacts,
  rollupCategoryFacts,
  rollupSourcesLabel,
} from "../../lib/dedupeFacts";
import { formatFactDate } from "../../lib/factMeta";
import { summarizeCount } from "../../lib/sectionSummary";
import { isPlaceholderParty, type PartyUi } from "../../lib/parties";
import FactCard from "./FactCard";
import OverviewSection from "./OverviewSection";
import { AiMark } from "./ProvenanceMark";

// A standalone overview section rendering a deduplicated set of facts of a
// single category — coverage terms, valuations, or background history. No
// total: these are reference facts (policy rules, assessed values, history),
// not additive costs. Collapse state is owned by the parent (Round 13) and
// the section renders through the shared OverviewSection shell with a counted
// ambient summary row.
//
// Round 9: after dedup, the SAME term restated per record (identical signed
// amount + overlapping title tokens) rolls up to one entry "stated in N
// records" — display-only, all quotes reachable, different amounts never
// roll up (see rollupCategoryFacts).
export default function FactCategorySection({
  id,
  title,
  facts,
  recordById,
  onNavigateToRecord,
  onViewQuote,
  open,
  onToggle,
  countNoun = "fact",
  keyOf,
  partyUi,
}: {
  id: string;
  title: string;
  facts: RecordFact[];
  recordById: Map<string, CaseRecord>;
  onNavigateToRecord?: (recordId: string) => void;
  onViewQuote?: (fact: RecordFact) => void;
  open: boolean;
  onToggle: () => void;
  // Noun for the ambient count in the collapsed summary ("12 terms").
  countNoun?: string;
  // Override the dedup key. Defaults to verbatim-quote matching; money-based
  // sections (coverage, valuation) pass amountDedupKey so "$35"/"$35.00"
  // collapse together.
  keyOf?: (f: RecordFact) => string;
  partyUi?: PartyUi;
}) {
  const rolled = rollupCategoryFacts(dedupeFacts(facts, recordById, keyOf), recordById);
  if (rolled.length === 0) return null;

  const summary = summarizeCount(rolled.length, countNoun);

  const cards = rolled.map(({ fact: f, alsoIn, corroborations, recordCount, parties }) => {
    // Aggregate party line: canonicalized, placeholder-free, deduped
    // (Round 10, 1A/1B). Falls back to a plain placeholder filter when no
    // index is supplied (tests, exports).
    const partyLine = partyUi
      ? partyUi.list(parties)
      : [...new Set(parties.filter((p) => !isPlaceholderParty(p)))];
    return (
      <div key={f.id}>
        <FactCard
          fact={f}
          record={recordById.get(f.record_id)}
          onNavigateToRecord={onNavigateToRecord}
          onViewQuote={onViewQuote}
          alsoIn={alsoIn}
          recordById={recordById}
          partyUi={partyUi}
        />
        {corroborations.length > 0 && (
          <details className="fact-components fact-rollup-sources">
            <summary>
              {rollupSourcesLabel(recordCount, corroborations.length)}
              {partyLine.length > 1 ? ` · parties: ${partyLine.join(", ")}` : ""}
            </summary>
            <ul>
              {corroborations.map((c) => {
                const rec = recordById.get(c.fact.record_id);
                const cParty = c.fact.party
                  ? partyUi?.display(c.fact.party) ?? c.fact.party
                  : null;
                return (
                  <li key={c.fact.id}>
                    “{c.fact.verbatim_quote}”{cParty ? ` — ${cParty}` : ""}{" "}
                    <button
                      type="button"
                      className="fact-source-link"
                      onClick={() => onNavigateToRecord?.(c.fact.record_id)}
                    >
                      {rec?.subject?.trim() || "Record"}
                      {rec?.record_date
                        ? ` (${formatFactDate(rec.record_date.slice(0, 10))})`
                        : ""}
                    </button>
                  </li>
                );
              })}
            </ul>
          </details>
        )}
      </div>
    );
  });

  return (
    <OverviewSection
      id={id}
      title={`${title} `}
      summary={summary}
      open={open}
      onToggle={onToggle}
      aiMark={<AiMark what="Section grouping (fact categorization)" />}
    >
      {cards}
    </OverviewSection>
  );
}
